import React, { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createCategory,
  updateCategory,
  type CreateCategoryPayload,
} from '@/api/categoryApi'
import { CATEGORY_QUERY_KEYS } from '@/api/queryKeys'
import { showErrorToast, showSuccessToast } from '../ui/toast'
import axios from 'axios'
import { CATEGORY_STATUS } from '@/constants'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface AddCategoryDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (categoryData: CategoryFormData) => void
  data?: CategoryFormData
}

const MAX_DESC_LENGTH = 255

export interface CategoryFormData {
  id?: string
  name: string
  description?: string
  status: 'active' | 'inactive'
  events?: number
}

const AddCategoryDrawer: React.FC<AddCategoryDrawerProps> = ({
  open,
  onOpenChange,
  data,
}) => {
  const [formData, setFormData] = useState<CategoryFormData>({
    name: data ? data.name : '',
    description: data ? data.description : '',
    status: data ? data.status : 'inactive',
  })
  const [initialFormData, setInitialFormData] =
    useState<CategoryFormData | null>(null)
  const [errors, setErrors] = useState<Partial<CategoryFormData>>({})
  const queryClient = useQueryClient()

  const resetForm = useCallback(() => {
    const resetData = {
      name: data?.name ?? '',
      description: data?.description ?? '',
      status: data?.status ?? 'inactive',
    }
    setFormData(resetData)
    setInitialFormData(resetData)
    setErrors({})
  }, [data])

  useEffect(() => {
    if (open) {
      if (data?.id) {
        resetForm()
      } else {
        const emptyForm = {
          name: '',
          description: '',
          status: 'inactive' as const,
        }
        setFormData(emptyForm)
        setInitialFormData(null)
        setErrors({})
      }
    }
  }, [open, resetForm, data?.id])

  const { mutate: createCategoryMutation, isPending: creatingCategory } =
    useMutation({
      mutationFn: createCategory,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CATEGORY_QUERY_KEYS.all] })

        showSuccessToast(
          'Your new category has been created.',
          `Category is now available for event organizers to use.`
        )

        onOpenChange(false)
        handleCancel()
      },
      onError: error => {
        if (axios.isAxiosError(error)) {
          const message =
            error?.response?.data?.message || 'Failed to create category'
          showErrorToast('Category Creation Failed', message)
        }
      },
    })

  const { mutate: updateCategoryMutation, isPending: updatingCategory } =
    useMutation({
      mutationFn: updateCategory,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CATEGORY_QUERY_KEYS.all] })

        showSuccessToast(
          'Your new category has been updated.',
          `Category is now available for event organizers to use.`
        )
      },
      onError: error => {
        if (axios.isAxiosError(error)) {
          const message =
            error?.response?.data?.message || 'Failed to create category'
          showErrorToast('Category Creation Failed', message)
        }
      },
    })

  const handleInputChange = (field: keyof CategoryFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<CategoryFormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required'
    }

    if (formData.description && formData.description.length > MAX_DESC_LENGTH) {
      newErrors.description = `Description cannot exceed ${MAX_DESC_LENGTH} characters`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      const payload: CreateCategoryPayload = {
        name: formData.name,
        ...(formData.description &&
          formData.description.trim() !== '' && {
            description: formData.description,
          }),
        status: formData.status,
      }

      try {
        if (data?.id) {
          const updatePayload = {
            id: data.id,
            payload,
          }
          updateCategoryMutation(updatePayload)
        } else {
          createCategoryMutation(payload)
        }
      } catch (error) {
        console.error('Event creation error:', error)
        showErrorToast('Event Creation Failed', 'Failed to create event')
      }
    }
  }

  const handleCancel = () => {
    resetForm()
    onOpenChange(false)
  }

  const descLength = formData.description?.length ?? 0
  const isFormValid =
    formData.name.trim().length > 0 && descLength <= MAX_DESC_LENGTH

  let buttonText: string
  if (data?.id) {
    buttonText = updatingCategory ? 'Saving category...' : 'Save Category'
  } else {
    buttonText = creatingCategory ? 'Adding category...' : 'Add Category'
  }

  const hasChanges = useCallback((): boolean => {
    if (!data?.id || !initialFormData) {
      return false
    }

    return (
      formData.name !== initialFormData.name ||
      formData.description !== initialFormData.description ||
      formData.status !== initialFormData.status
    )
  }, [formData, initialFormData, data?.id])

  const isEditMode = !!data?.id
  const shouldDisableButton =
    !isFormValid ||
    creatingCategory ||
    updatingCategory ||
    (isEditMode && !hasChanges())

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent
        className="w-full sm:w-[400px] h-full bg-ss-neutral-white border-l border-ss-neutral-50 rounded-none lg:rounded-2xl flex flex-col"
        aria-describedby="add-category-description"
      >
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-ss-neutral-50/60">
          <DrawerTitle
            className="text-lg font-semibold text-ss-neutral-950 leading-7"
            id="add-category-title"
          >
            {data?.id ? 'Edit Category' : 'Add New Category'}
          </DrawerTitle>
          <DrawerClose asChild>
            <button
              className="h-5 w-5 text-ss-neutral-600 hover:text-ss-neutral-950 transition-colors focus:outline-none focus:ring-2 focus:ring-ss-blue-500 focus:ring-offset-2 rounded-sm"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </DrawerClose>
        </DrawerHeader>

        {/* Form Content */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
          id="add-category-description"
        >
          {/* Category Name Field */}
          <div className="space-y-2">
            <Label
              htmlFor="category-name"
              variant="required"
              className="text-sm font-semibold text-ss-neutral-950"
            >
              Category Name
            </Label>
            <Input
              id="category-name"
              type="text"
              placeholder="Enter category name"
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              className={cn(
                'h-11 px-3 py-2 bg-ss-neutral-white border-ss-neutral-100 text-ss-neutral-950 placeholder:text-ss-neutral-400 rounded-lg',
                'focus:border-ss-blue-700 focus:ring-2 focus:ring-ss-blue-500/20',
                'hover:border-ss-blue-700',
                errors.name &&
                  'border-ss-critical-200 focus:border-ss-critical-200 focus:ring-ss-critical-200/20'
              )}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'category-name-error' : undefined}
              required
            />
            {errors.name && (
              <p
                id="category-name-error"
                className="text-sm text-ss-critical-200"
                role="alert"
              >
                {errors.name}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label
              htmlFor="category-description"
              className="text-sm font-semibold text-ss-neutral-950"
            >
              Description
            </Label>
            <Textarea
              id="category-description"
              placeholder="Add a clear and engaging description of your category."
              value={formData.description}
              onChange={e => handleInputChange('description', e.target.value)}
              className={cn(
                'min-h-[105px] px-3 py-3 bg-ss-neutral-white border-ss-neutral-100 text-ss-neutral-950 placeholder:text-ss-neutral-400 rounded-lg resize-none',
                'focus:border-ss-blue-700 focus:ring-2 focus:ring-ss-blue-500/20',
                'hover:border-ss-blue-700',
                errors.description &&
                  'border-ss-critical-200 focus:border-ss-critical-200 focus:ring-ss-critical-200/20'
              )}
              maxLength={MAX_DESC_LENGTH}
              rows={4}
            />
            <p className="text-xs text-ss-neutral-500">
              {formData.description?.length}/{MAX_DESC_LENGTH} characters
            </p>
            {errors.description && (
              <p className="text-sm text-ss-critical-200" role="alert">
                {errors.description}
              </p>
            )}
          </div>

          {/* Category Status Toggle */}
          <div className="flex items-center justify-between">
            <Label
              htmlFor="category-status"
              className="text-sm font-semibold text-ss-neutral-950"
            >
              Category Status
            </Label>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Switch
                        id="category-status"
                        checked={formData.status === CATEGORY_STATUS.ACTIVE}
                        onCheckedChange={checked => {
                          if (data?.events && data.events > 0 && !checked) {
                            // Prevent deactivating if category has events
                            return
                          }
                          handleInputChange(
                            'status',
                            checked
                              ? CATEGORY_STATUS.ACTIVE
                              : CATEGORY_STATUS.INACTIVE
                          )
                        }}
                        disabled={Boolean(
                          data?.events &&
                            data.events > 0 &&
                            formData.status === CATEGORY_STATUS.ACTIVE
                        )}
                        className={cn(
                          'h-[22px] w-10 data-[state=unchecked]:bg-ss-neutral-200',
                          'data-[state=checked]:bg-ss-blue-500'
                        )}
                        aria-label={
                          formData.status === CATEGORY_STATUS.ACTIVE
                            ? 'Category is active'
                            : 'Category is inactive'
                        }
                      />
                    </div>
                  </TooltipTrigger>
                  {data?.events !== undefined &&
                    data.events > 0 &&
                    formData.status === CATEGORY_STATUS.ACTIVE && (
                      <TooltipContent side="left">
                        <p className="text-xs">
                          Cannot deactivate category with {data.events} active{' '}
                          {data.events === 1 ? 'event' : 'events'}
                        </p>
                      </TooltipContent>
                    )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>

        {/* Footer with buttons */}
        <DrawerFooter className="p-4 border-t border-ss-neutral-50 bg-ss-neutral-white shadow-lg">
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleCancel}
              className={cn(
                'flex-1 h-9 border-ss-neutral-200 text-ss-neutral-950 bg-transparent',
                'hover:bg-ss-action-gray-50 hover:border-ss-neutral-300',
                'focus:ring-2 focus:ring-ss-blue-500/20 focus:border-ss-blue-700'
              )}
              aria-label="Cancel and close drawer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={shouldDisableButton}
              className={cn(
                'flex-1 h-9 font-semibold',
                isFormValid
                  ? 'bg-ss-blue-900 text-ss-neutral-white hover:bg-ss-blue-800 focus:ring-2 focus:ring-ss-blue-500/20'
                  : 'bg-ss-blue-100 text-ss-neutral-25 cursor-not-allowed'
              )}
              aria-label="Add new category"
            >
              {buttonText}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default AddCategoryDrawer
